import { Component, CUSTOM_ELEMENTS_SCHEMA, Input, OnChanges, OnInit, SimpleChanges, ViewChild, ElementRef, OnDestroy } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { PDFSource, PdfViewerModule } from 'ng2-pdf-viewer';
import { PayablesService } from '../../../../../services/payables/payables.service';
import { DomSanitizer } from '@angular/platform-browser';
import { UploadedFile } from '../../../../../models/entities/uploaded-file';
import { CommonModule } from '@angular/common';
import { renderAsync } from 'docx-preview';

@UntilDestroy()
@Component({
  selector: 'app-payable-attachment-preview',
  templateUrl: './payable-attachment-preview.component.html',
  styleUrls: ['./payable-attachment-preview.component.scss'],
    standalone: true,
    imports: [
        CommonModule,
        PdfViewerModule,
    ],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class PayableAttachmentPreviewComponent implements OnInit, OnChanges, OnDestroy {
    @Input() attachment: UploadedFile;
    @Input() containerHeight: string;
    @ViewChild('docxContainer', { static: false }) docxContainer: ElementRef;
    currentSrc: string | PDFSource | ArrayBuffer;
    fileLoading: boolean = true;
    // A file that will not load is a fact about this box, so it is said inside it.
    loadFailed: boolean = false;

    private readonly BINARY_DOC_TYPES = ['docx'];

    /**
     * The types the default template branch can actually render, which it does with an <img> tag.
     *
     * SD-8230: core now serves every stored type rather than throwing on the ones it had no case for, and
     * PayablesService asks for those as arraybuffer so the response is consumable. Without this list the
     * newly-downloadable types (xls, xlsx, doc, csv, rtf, ...) would land in the <img> branch and render a
     * broken image with no error, replacing a visible failure with a silent one.
     */
    private readonly PREVIEWABLE_IMAGE_TYPES = ['jpg', 'jpeg', 'png', 'gif'];
    constructor(
        private _payablesService: PayablesService,
        private _sanitizer: DomSanitizer,
    ) { }
    get attachmentType(): string {
        if(this.attachment){
            return this.attachment.fileType;
        }else{
            return '';
        }
    }
    get isPreviewableImage(): boolean {
        return this.PREVIEWABLE_IMAGE_TYPES.indexOf(this.attachmentType) !== -1;
    }

    /**
     * Whether this attachment has an inline viewer at all. Anything else is downloadable but not
     * displayable, and is told so rather than shown a broken preview (SD-8230).
     */
    get isPreviewable(): boolean {
        return this.attachmentType === 'pdf'
            || this.attachmentType === 'txt'
            || this.BINARY_DOC_TYPES.indexOf(this.attachmentType) !== -1
            || this.isPreviewableImage;
    }

    ngOnInit(): void {
      this.getAttachment();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.attachment && !changes.attachment.firstChange) {
            this.getAttachment();
        }
    }

    getAttachment() {
        this.fileLoading = true;
        this.loadFailed = false;

        // SD-8230: no viewer for this type, so don't fetch it only to render it into an <img>.
        if (!this.isPreviewable) {
            this.currentSrc = null;
            this.fileLoading = false;
            return;
        }

        if (this.BINARY_DOC_TYPES.includes(this.attachmentType)) {
            this._payablesService.getInvoice(this.attachment)
                .pipe(untilDestroyed(this))
                .subscribe({
                    next: (data: ArrayBuffer) => {
                        this.fileLoading = false;

                        setTimeout(() => {
                            if (this.docxContainer?.nativeElement) {
                                renderAsync(data, this.docxContainer.nativeElement, undefined, {
                                    className: 'docx',
                                    inWrapper: false,
                                    ignoreWidth: true,
                                    ignoreHeight: true,
                                });
                            }
                        }, 0);
                    },
                    error: (error) => {
                        console.error('Error retrieving file:', error);
                        this.loadFailed = true;
                        this.fileLoading = false;
                    },
                });
            return;
        } else if (this.attachmentType === 'pdf') {
            this._payablesService.getInvoice(this.attachment)
                .pipe(untilDestroyed(this))
                .subscribe({
                    next: (attachmentSrc) => {
                        this.fileLoading = false;
                        this.currentSrc = attachmentSrc;
                    },
                    error: () => {
                        this.loadFailed = true;
                        this.fileLoading = false;
                    },
                });
        } else {
            this._payablesService.getInvoice(this.attachment)
                .pipe(untilDestroyed(this))
                .subscribe({
                    next: (data) => {
                        if (this.attachmentType === 'txt') {
                            this.currentSrc = data;
                            this.fileLoading = false;
                            return;
                        }
                        const base64Content = data;
                        // Determine the appropriate data URL prefix based on the file type
                        const dataUrlPrefix = this.getDataUrlPrefix(this.attachmentType);
                        // Create the data URL
                        this.currentSrc = this._sanitizer.bypassSecurityTrustUrl(`${dataUrlPrefix}${base64Content}`);
                        this.fileLoading = false;
                    },
                    error: () => {
                        this.loadFailed = true;
                        this.fileLoading = false;
                    },
                });
        }
    }
    private getDataUrlPrefix(fileType: string): string {
        switch (fileType) {
            case 'png':
            case 'jpg':
            case 'jpeg':
            case 'gif':
                return `data:image/${fileType};base64,`;
            case 'txt':
                return 'data:text/plain;base64,';
        }
    }

    ngOnDestroy(): void {
        if (this.docxContainer?.nativeElement) {
            this.docxContainer.nativeElement.innerHTML = '';
        }
    }
}
